import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Header, StickyContactHeader } from '@/components/Header';
import { Footer, FeedbackBanner } from '@/components/Footer';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { HeroSlider } from '@/components/HeroSlider';
import { CategorySection } from '@/components/CategorySection';
import { BrandShowcase } from '@/components/BrandShowcase';
import { FeaturesSection } from '@/components/FeaturesSection';
import { HowItWorks } from '@/components/HowItWorks';
import { TestimonialsSection } from '@/components/TestimonialsSection';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const HomePage = () => {
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [seo, setSeo] = useState({});

  useEffect(() => {
    const fetchSeo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/content/seo`);
        if (res.ok) {
          const data = await res.json();
          if (data) setSeo(data);
        }
      } catch (e) { console.error(e); }
    };
    fetchSeo();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      // Show sticky contact header after scrolling past 80% of the page
      const scrollPercentage = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      setShowStickyHeader(scrollPercentage > 80);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {seo.title && (
        <Helmet>
          <title>{seo.title}</title>
          {seo.description && <meta name="description" content={seo.description} />}
          {seo.keywords && <meta name="keywords" content={seo.keywords} />}
        </Helmet>
      )}
      <StickyContactHeader isVisible={showStickyHeader} />
      
      <main>
        {/* Hero Slider - Samsung Business Style */}
        <HeroSlider />
        
        {/* Categories Section */}
        <CategorySection />
        
        {/* Brand Showcase */}
        <BrandShowcase />
        
        {/* Features Section */}
        <FeaturesSection />
        
        {/* How It Works */}
        <HowItWorks />
        
        {/* Testimonials / Google Reviews */}
        <TestimonialsSection />
        
        {/* Social Media Section */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-3">
                Sosyal Medya
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-display text-foreground mb-4">
                Bizi Takip Edin
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Güncel içeriklerimiz, onarım videoları ve kampanyalarımız için sosyal medya hesaplarımızı takip edin
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {/* YouTube */}
              <a
                href="https://www.youtube.com/@teslaprotr"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-card rounded-2xl p-8 text-center border shadow-card hover:shadow-lg hover:border-red-500/50 transition-all"
              >
                <div className="w-20 h-20 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-red-500 group-hover:scale-110 transition-all">
                  <svg className="w-10 h-10 text-red-500 group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold font-display text-foreground mb-2">YouTube</h3>
                <p className="text-sm text-muted-foreground">Onarım videoları ve teknik içerikler</p>
              </a>

              {/* TikTok */}
              <a
                href="https://www.tiktok.com/@teslaprotr"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-card rounded-2xl p-8 text-center border shadow-card hover:shadow-lg hover:border-foreground/50 transition-all"
              >
                <div className="w-20 h-20 rounded-2xl bg-foreground/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-foreground group-hover:scale-110 transition-all">
                  <svg className="w-10 h-10 text-foreground group-hover:text-background transition-colors" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold font-display text-foreground mb-2">TikTok</h3>
                <p className="text-sm text-muted-foreground">Kısa ve eğlenceli içerikler</p>
              </a>

              {/* Instagram */}
              <a
                href="https://www.instagram.com/teslaprotr/"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-card rounded-2xl p-8 text-center border shadow-card hover:shadow-lg hover:border-pink-500/50 transition-all"
              >
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-orange-500/10 flex items-center justify-center mx-auto mb-4 group-hover:from-purple-500 group-hover:via-pink-500 group-hover:to-orange-500 group-hover:scale-110 transition-all">
                  <svg className="w-10 h-10 text-pink-500 group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold font-display text-foreground mb-2">Instagram</h3>
                <p className="text-sm text-muted-foreground">Güncel paylaşımlar ve hikayeler</p>
              </a>
            </div>
          </div>
        </section>
        
        {/* Feedback & Contact Banner */}
        <FeedbackBanner />
      </main>
      
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default HomePage;
